<template>
  <div class="emoji-input-container">
    <!-- 可编辑区域 -->
    <div ref="editor" class="twemoji-editor" contenteditable="true" @keydown="handleKeydown" @input="handleInput"
      @click="handleEditorClick" @compositionstart="handleComposition" @compositionupdate="saveSelection"
      @compositionend="handleComposition"></div>
    <!-- 动态placeholder -->
    <div v-if="showPlaceholder" class="placeholder-overlay">{{ placeholderText }}</div>
    <!-- 表情触发按钮 -->
    <!-- <button class="trigger-button" @click="togglePicker">😀</button> -->

    <!-- 表情选择器 -->
    <div v-if="showPicker" class="twemoji-picker">
      <div class="picker-header">
        <span class="picker-title">{{ $t('select_emoji') }}</span>
        <button class="close-btn" @click="showPicker = false">×</button>
      </div>
      <!-- 分类导航 -->
      <div class="category-tabs">
        <button v-for="category in filteredCategories" :key="category" :class="{ active: activeCategory === category }"
          @click="setCategory(category)">
          {{ categoryIcons[category] || '◻' }}
        </button>
      </div>

      <!-- 表情展示区 -->
      <div class="emoji-grid">
        <span v-for="emoji in filteredEmojis" :key="emoji.unicode" class="emoji-option"
          @click="insertEmoji(emoji.unicode)" v-html="parseEmoji(emoji.unicode)"></span>
      </div>
    </div>

    <!-- 隐藏的原始数据存储 -->
    <input type="hidden" :value="rawContent" />
  </div>
</template>

<script>
import { debounce } from 'lodash'
import twemoji from 'twemoji'
import emojiData from 'emoji-datasource/emoji.json'

export default {
  props: {
    placeholder: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      hasFocus: false,
      isEmpty: true,
      rawContent: '',
      showPicker: false,
      searchQuery: '',
      activeCategory: 'Smileys & Emotion',
      savedSelection: null,
      isComposing: false, // 是否正在输入法输入中
      categories: [
        'Smileys & Emotion',
        'People & Body',
        'Animals & Nature',
        'Food & Drink',
        'Travel & Places',
        'Activities',
        'Objects',
        'Symbols',
        'Flags'
      ],
      categoryIcons: {
        'Smileys & Emotion': '😀',
        'People & Body': '👋',
        'Animals & Nature': '🐻',
        'Food & Drink': '🍔',
        'Travel & Places': '✈️',
        'Activities': '⚽',
        'Objects': '💡',
        'Symbols': '❤️',
        'Flags': '🏳️'
      },
      allEmojis: []
    }
  },

  computed: {
    // 计算显示状态
    showPlaceholder() {
      return !this.hasFocus && this.isEmpty;
    },
    // 支持上层传入的placeholder
    placeholderText() {
      return this.placeholder || this.$t('placeholder_default');
    },
    // 处理后的显示内容
    processedContent() {
      return twemoji.parse(this.rawContent, {
        className: 'twemoji',
        base: this.$publicPath + 'static/twemoji/',
        size: 'svg',
        ext: '.svg'
      })
    },

    // 过滤后的表情列表
    filteredEmojis() {
      return this.allEmojis.filter(emoji => {
        const matchSearch = emoji.short_name.includes(this.searchQuery.toLowerCase()) ||
          emoji.keywords.some(kw => kw.includes(this.searchQuery.toLowerCase()))

        return this.activeCategory === 'All' || emoji.category === this.activeCategory
      })
    },

    // 分类导航列表
    filteredCategories() {
      return ['All', ...this.categories]
    }
  },

  created() {
    this.initializeEmojis()
    this.debouncedSync = debounce(this.syncContent, 100)
  },

  mounted() {
    this.observer = new MutationObserver((mutations) => {
      this.syncContent();
    });

    this.observer.observe(this.$refs.editor, {
      childList: true,
      subtree: true,
      characterData: true
    });
    this.checkEmptiness();
    // 添加自动聚焦
    // this.$refs.editor.focus();
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('selectionchange', this.saveSelection);
  },
  methods: {
    // 新增焦点处理方法
    handleFocus() {
      this.hasFocus = true;
    },
    handleBlur() {
      this.hasFocus = false;
      this.checkEmptiness();
    },

    // 检查编辑器是否为空
    checkEmptiness() {
      if (!this.$refs.editor) return;

      // 检查文本内容
      const hasText = this.$refs.editor.innerText.trim() !== '';

      // 检查表情（通过img标签）
      const hasEmojis = this.$refs.editor.querySelectorAll('img.twemoji').length > 0;

      this.isEmpty = !hasText && !hasEmojis;
    },
    // 处理键盘事件
    handleKeydown(event) {
      // 处理 Enter 键（单行输入，回车直接发送）
      if (event.key === 'Enter') {
        event.preventDefault();
        // 输入法组合中回车不发送（isComposing 兜底 event.isComposing，防止 composition 事件丢失）
        if (this.isComposing || event.isComposing) return;
        this.$emit('enter');
        return;
      }
    },

    getRawContent() {
      const editor = this.$refs.editor
      if (!editor) return ''

      // 克隆编辑器内容
      const clone = editor.cloneNode(true)

      // 处理所有 twemoji img，替换为 emoji 字符
      const imgs = clone.querySelectorAll('img.twemoji, img[src*="/twemoji/"]')
      imgs.forEach(img => {
        const emojiChar = this.extractEmojiFromImg(img)
        if (emojiChar) {
          const textNode = document.createTextNode(emojiChar)
          img.parentNode.replaceChild(textNode, img)
        }
      })

      // 获取纯文本内容
      let result = clone.innerHTML
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<div>/gi, '\n')
        .replace(/<\/div>/gi, '')
        .replace(/<p>/gi, '')
        .replace(/<\/p>/gi, '')

      // 解码 HTML 实体
      const textarea = document.createElement('textarea')
      textarea.innerHTML = result
      result = textarea.value

      return result
    },

    // 从img标签提取Unicode字符
    extractEmojiFromImg(img) {
      // 从 alt 属性获取
      let alt = img.getAttribute('alt')
      if (alt && alt.trim()) {
        return alt.trim()
      }
      // 有些浏览器可能用 img.alt 属性
      if (img.alt && img.alt.trim()) {
        return img.alt.trim()
      }

      // 备用方案：从文件名解析 (如 1f609.svg -> 😉)
      const src = img.getAttribute('src') || img.src || ''
      if (src.includes('/twemoji/')) {
        const match = src.match(/\/([0-9a-fA-F-]+)\.svg$/)
        if (match && match[1]) {
          try {
            return match[1].split('-')
              .map(cp => String.fromCodePoint(parseInt(cp, 16)))
              .join('')
          } catch (e) {
            console.warn('[extractEmojiFromImg] Failed:', e)
          }
        }
      }

      return null
    },
    // 新增方法
    clearContentAndBlur() {
      const editor = this.$refs.editor
      if (!editor) return

      // 清空内容
      editor.innerHTML = ''

      // 强制触发内容更新
      this.rawContent = ''
      this.$emit('input', '') // 如果需要同步到v-model

      // 失焦处理
      editor.blur()

      // 兼容移动端键盘收起
      if (document.activeElement === editor) {
        document.activeElement.blur()
      }

      // 清除选区
      window.getSelection().removeAllRanges()

      this.showPicker = false
      this.checkEmptiness()
    },
    // 设置编辑器内容
    setContent(content) {
      const editor = this.$refs.editor
      if (!editor) {
        return
      }

      // 将换行符转换为 <br> 标签
      const htmlContent = content.replace(/\n/g, '<br>')
      editor.innerHTML = htmlContent

      // 同步内容
      this.rawContent = htmlContent
      this.$emit('input', content)

      // 解析 emoji
      this.$nextTick(() => {
        this.syncContent()
        this.checkEmptiness()
        // 将光标移到末尾
        this.moveCursorToEnd()
      })
    },
    /* 初始化方法 */
    initializeEmojis() {
      this.allEmojis = emojiData
        .filter(e => {
          // 1. 只要 unified 里有 '-'，就排除（即多 codepoint）
          if (e.unified.includes('-')) return false;
          // 2. 排除含 ZWJ/变体/肤色的 emoji
          const codepoints = e.unified.split('-');
          // ZWJ: 200D, 变体: FE0F, 肤色: 1F3FB~1F3FF
          if (
            codepoints.includes('200D') ||
            codepoints.includes('FE0F') ||
            codepoints.some(cp => {
              const n = parseInt(cp, 16);
              return n >= 0x1F3FB && n <= 0x1F3FF;
            })
          ) return false;
          // 3. 其它正常 emoji
          return !e.obsoleted_by;
        })
        .map(emoji => ({
          unicode: this.unifiedToUnicode(emoji.unified),
          short_name: emoji.short_name,
          keywords: emoji.keywords,
          category: emoji.category,
          sortOrder: emoji.sort_order
        }))
        .sort((a, b) => a.sortOrder - b.sortOrder)
    },

    setupTouchSupport() {
      this.$refs.editor.addEventListener('touchstart', this.saveSelection)
    },

    /* 核心功能方法 */
    insertEmoji(emoji) {
      // 先 focus 到编辑器
      this.$refs.editor.focus()
      // 恢复上次保存的选区
      this.restoreSelection()
      // 再插入 emoji
      this.insertEmojiChar(emoji)
      this.$nextTick(() => {
        this.saveSelection() // 插入后保存新选区
        this.syncContent()
      })
      this.checkEmptiness();
    },

    // 修改后的 insertEmojiChar 方法
    insertEmojiChar(emojiUnicode) {
      const svg = twemoji.parse(emojiUnicode, {
        className: 'twemoji',
        base: this.$publicPath + 'static/twemoji/',
        size: 'svg',
        ext: '.svg'
      });
      const div = document.createElement('div');
      div.innerHTML = svg;
      const emojiNode = div.firstChild;

      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);

      // 创建文档片段一次性插入
      const fragment = document.createDocumentFragment();
      fragment.appendChild(emojiNode);
      fragment.appendChild(document.createTextNode('\u200B')); // 零宽空格

      range.deleteContents(); // 删除原有选区内容
      range.insertNode(fragment);

      // 将光标定位到新插入的零宽空格之后
      const newRange = document.createRange();
      newRange.setStartAfter(emojiNode);
      newRange.collapse(true);

      selection.removeAllRanges();
      selection.addRange(newRange);
    },
    // 修改 syncContent 方法
    syncContent() {
      this.rawContent = this.$refs.editor.innerHTML;

      // 延迟解析避免干扰选区
      this.$nextTick(() => {
        // 先处理链接替换
        this.replaceLinks();

        twemoji.parse(this.$refs.editor, {
          className: 'twemoji',
          base: this.$publicPath + 'static/twemoji/',
          callback: () => {
            this.restoreSelection();
            this.$refs.editor.normalize(); // 合并相邻文本节点
          }
        });
      });
    },
    // 新增方法：仅解析新内容
    parseNewEmojis() {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = this.rawContent

      // 只解析尚未转换的 emoji
      twemoji.parse(tempDiv, {
        className: 'twemoji',
        base: this.$publicPath + 'static/twemoji/',
        size: 'svg',
        ext: '.svg'
      })

      // 仅当内容不同时才更新 DOM
      if (tempDiv.innerHTML !== this.$refs.editor.innerHTML) {
        this.$refs.editor.innerHTML = tempDiv.innerHTML
      }
    },

    // 新增方法：替换链接为可点击的链接
    replaceLinks() {
      if (!this.$refs.editor) return;

      // 获取编辑器中的所有文本节点
      const walker = document.createTreeWalker(
        this.$refs.editor,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );

      const textNodes = [];
      let node;
      while (node = walker.nextNode()) {
        textNodes.push(node);
      }

      // 对每个文本节点进行链接检测和替换
      textNodes.forEach(textNode => {
        const text = textNode.textContent;
        if (!text) return;

        // 链接匹配正则表达式 - 匹配 http/https 开头的链接
        // const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/g;
        // 滴滴拼团链接
        const urlRegex = /(https:\/\/page\.udache\.com\/quartz-mall\/goodsProcess\.html[^\s<>"]*)/g;
        // 检查是否包含链接
        if (urlRegex.test(text)) {
          // 重置正则表达式的 lastIndex
          urlRegex.lastIndex = 0;

          // 创建一个文档片段来存放替换后的内容
          const fragment = document.createDocumentFragment();
          let lastIndex = 0;
          let match;

          while ((match = urlRegex.exec(text)) !== null) {
            // 添加链接前的文本
            if (match.index > lastIndex) {
              fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
            }


            // 创建外层容器

            const container = document.createElement('span');
            container.style.display = 'inline-flex'; // 保持Flexbox布局但行为改为行内
            container.style.verticalAlign = 'middle';

            // const container = document.createElement('div');
            container.style.fontWeight = '600';
            // container.style.display = 'flex';
            container.style.alignItems = 'center';
            container.style.gap = '8px';

            // 创建图片图标 - 使用一个通用的优惠券/票券图标
            const imgIcon = document.createElement('img');
            imgIcon.src = '/static/icon/tuan_icon.png'; // 示例图标URL
            imgIcon.style.width = '24px'; // 控制图标大小
            imgIcon.style.height = '24px';

            // 创建链接元素
            const link = document.createElement('a');
            link.href = match[0];
            link.textContent = '帮我点此链接一起滴滴拼团';
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.style.color = '#3DAECF';
            link.style.textDecoration = 'underline';
            link.style.cursor = 'pointer';

            // 组合元素
            container.appendChild(imgIcon);
            container.appendChild(link);
            fragment.appendChild(container);

            // 创建链接元素
            // const link = document.createElement('a');
            // link.href = match[0];
            // link.textContent = '帮我点此链接一起滴滴拼团';
            // link.target = '_blank';
            // link.rel = 'noopener noreferrer';

            // // 添加链接样式
            // link.style.color = '#3DAECF';
            // link.style.textDecoration = 'underline';
            // link.style.cursor = 'pointer';

            // fragment.appendChild(link);

            // const linkDiv = document.createElement('div');
            // linkDiv.textContent = '帮我点此链接一起滴滴拼团';
            // linkDiv.setAttribute('data-url', match[0]);
            // linkDiv.style.color = '#3DAECF';
            // linkDiv.style.textDecoration = 'underline';
            // linkDiv.style.cursor = 'pointer';
            // linkDiv.style.display = 'inline';
            // fragment.appendChild(linkDiv);

            lastIndex = match.index + match[0].length;
          }

          // 添加剩余的文本
          if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
          }

          fragment.appendChild(document.createTextNode('\u200B'));

          // 用文档片段替换原始文本节点
          textNode.parentNode.replaceChild(fragment, textNode);
        }
      });
    },


    handleEditorClick() {
      this.saveSelection()
      this.$emit('editor-click')
    },
    saveSelection() {
      const sel = window.getSelection();
      if (sel.rangeCount > 0) {
        this.savedRange = sel.getRangeAt(0).cloneRange();
      }
    },
    restoreSelection() {
      if (this.savedRange) {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(this.savedRange);
      }
    },

    // 新增光标复位方法
    moveCursorToEnd() {
      const editor = this.$refs.editor;
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false); // 移动到末尾
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    },

    findTextNode(node) {
      if (!node || node === this.$refs.editor) {
        return this.$refs.editor.childNodes[0]
      }
      return node.nodeType === 3 ? node : this.findTextNode(node.firstChild)
    },

    /* 工具方法 */
    unifiedToUnicode(unified) {
      return unified.split('-')
        .map(u => String.fromCodePoint(parseInt(u, 16)))
        .join('')
    },

    parseEmoji(unicode) {
      return twemoji.parse(unicode, {
        className: 'picker-twemoji',
        base: this.$publicPath + 'static/twemoji/',
        size: 'svg',
        ext: '.svg'
      })
    },

    /* 事件处理 */
    // 修改 handleInput 方法
    handleInput: debounce(function (e) {
      // iOS Safari 在 contenteditable 中按回车不会触发 keydown，只会插入换行；
      // 检测到换行时按“回车发送”处理（删除换行并触发发送）。
      // 注意：粘贴/拖拽多行文本（inputType=insertFromPaste*/insertFromDrop）也会带换行，
      // 这种情况只展平为单行，不能替用户直接发送。
      if (this.$refs.editor && this.$refs.editor.innerText.indexOf('\n') !== -1) {
        if (!this.isComposing) {
          var text = this.getRawContent().replace(/\n+/g, ' ').trim()
          this.setContent(text)
          if (!this.isPasteInputEvent(e)) {
            this.$emit('enter')
          }
        }
        return
      }
      this.saveSelection();
      this.syncContent();
      this.restoreSelection();
      this.checkEmptiness();
    }, 50),

    // 换行是否来自粘贴/拖拽而非回车键：粘贴多行文本不应触发发送。
    // inputType 不存在（老浏览器不支持 InputEvent.inputType）时按回车处理，保持旧行为。
    isPasteInputEvent(e) {
      var inputType = e && e.inputType
      return inputType === 'insertFromPaste' ||
        inputType === 'insertFromPasteAsQuotation' ||
        inputType === 'insertFromDrop'
    },

    handleComposition(e) {
      if (e.type === 'compositionstart') {
        this.isComposing = true;
        this.saveSelection();
      } else if (e.type === 'compositionend') {
        this.isComposing = false;
        this.syncContent()
      }
    },

    /* UI控制 */
    togglePicker() {
      this.showPicker = !this.showPicker
      if (this.showPicker) {
        this.$nextTick(() => {
          this.$refs.editor.focus()
          // 可选：恢复光标
          this.restoreSelection()
        })
      }
    },
    setCategory(category) {
      this.activeCategory = category
      this.searchQuery = ''
    }
  }
}
</script>

<style scoped>
.placeholder-overlay {
  position: absolute;
  top: 4px;
  left: 14px;
  right: 40px;
  /* 避让右侧表情按钮 */
  color: #8A8A8A;
  pointer-events: none;
  /* 允许点击穿透到编辑器 */
  font-size: 0.8rem;
  line-height: 28px;
  /* 与编辑器单行行高一致 */
  z-index: 2;
  /* 显示在编辑器上方 */
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* 保持原有样式不变 */
.emoji-input-container {
  position: relative;
  width: 100%;
  /* max-width: 600px; */
}

.twemoji-editor {
  border: none;
  border-radius: 18px;
  overflow-x: auto;
  overflow-y: hidden;
  font-size: 0.85rem;
  padding: 4px 14px;
  line-height: 28px;
  background: transparent;
  height: 28px;
  text-align: left;
  font-weight: 600;
  word-break: break-all;
  white-space: nowrap;
}

.twemoji-editor:focus {
  outline: none;
  /* 移除默认轮廓线 */
  border: none !important;
  /* 保持无边框 */
  box-shadow: none;
  /* 移除可能存在的阴影效果 */
}

.twemoji {
  width: 24px;
  height: 24px;
  vertical-align: middle;
  margin: 0 2px;
}

.trigger-button {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
}

.twemoji-picker {
  position: absolute;
  bottom: 100%;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-bottom: 10px;
}

.picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid #E0DDD8;
}

.picker-title {
  font-size: 14px;
  color: #1A1A1A;
  font-weight: 500;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #8A8A8A;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  line-height: 1;
}

.close-btn:hover {
  background: #E0DDD8;
  color: #8A8A8A;
}

.category-tabs {
  display: flex;
  padding: 8px;
  border: 1px solid #E0DDD8;
}

.category-tabs button {
  flex: 1;
  padding: 8px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.2s;
}

.category-tabs button.active {
  background: #E0DDD8;
  border-radius: 6px;
}

.emoji-grid {
  max-height: 120px;
  overflow-y: auto;
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
}

.emoji-option {
  cursor: pointer;
  padding: 3px;
  border-radius: 4px;
  transition: background 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
}

.emoji-option:hover {
  background: #E0DDD8;
}

.emoji-input-container>>>.picker-twemoji {
  width: 24px;
  height: 24px;
  display: block;
}

/* 强制覆盖所有层级 */
.emoji-input-container>>>.twemoji {
  width: 20px !important;
  height: 20px !important;
  position: relative;
  top: 6px;
  padding: 2px;
}

/* 深度选择器处理SVG */
.emoji-input-container>>>.twemoji svg {
  width: 100% !important;
  height: 100% !important;
  vertical-align: top;
  /* 修复对齐问题 */
}

/* 链接样式 */
.emoji-input-container>>>a {
  color: #3DAECF;
  text-decoration: underline;
  cursor: pointer;
  word-break: break-all;
  display: inline-block;
  max-width: 100%;
}

.emoji-input-container>>>a:hover {
  color: #2A8FAD;
  text-decoration: underline;
}
</style>


// WEBPACK FOOTER //
// src/components/EmojiInputArea.vue